import EventLayout from "@/components/layouts/EventLayout";
import TabbedPage from "@/components/shared/TabbedPage";
import { TabIconLabel } from "@/components/shared/TabIconLabel";
import { useState } from "react";
import { usePage } from "@inertiajs/react";
import { HiOutlineCog, HiOutlineDesktopComputer, HiOutlineDeviceMobile, HiOutlineClipboardList, HiOutlineColorSwatch } from "react-icons/hi";
import PreLuckyDrawSettings from "./components/PreLuckyDrawSettings";
import DrawOnBigScreen from "./components/DrawOnBigScreen";
import DrawOnAttendeeApp from "./components/DrawOnAttendeeApp";
import WinnersList from "./components/WinnersList";
import LuckyDrawAppearanceSettings, {
  type LuckyDrawAppearance,
} from "./components/LuckyDrawAppearanceSettings";

type EligibilityRule = {
  id: string;
  name: string;
  scanPointId: string;
  checked: boolean;
};

type Participant = {
  id: string;
  name: string;
  email: string;
  isWinner: boolean;
  registrantId?: string;
};

type Category = {
  id: string;
  name: string;
  prizeName: string;
  winnerCount: number;
  participants: Participant[];
};

type Winner = {
  id: string;
  name: string;
  email: string;
  prize: string;
  category: string;
  categoryId?: string;
  registrantId?: string;
};

type Attendee = {
  id: string;
  name: string;
  email: string;
};

type FilterFieldOption = {
  id: number;
  label: string;
  value: string;
};

type FilterFieldGroup = {
  fieldId: number;
  label: string;
  identifier: string;
  options: FilterFieldOption[];
};

type FilterOptions = {
  fields: FilterFieldGroup[];
};

type PageProps = {
  eventId: number;
  eventName: string;
  eventSlug: string;
  eligibilityRules: EligibilityRule[];
  filterOptions: FilterOptions;
  fieldSelections: Record<string, string[]>;
  categories: Category[];
  winners: Winner[];
  attendees: Attendee[];
  eligibleRegistrantCount?: number;
  selfRevealEnabled: boolean;
  luckyDrawAppearance?: LuckyDrawAppearance;
};

const LuckyDrawIndex = () => {
  const {
    eventSlug,
    eligibilityRules: initialRules = [],
    filterOptions = { fields: [] },
    fieldSelections: initialFieldSelections = {},
    categories: initialCategories = [],
    winners: initialWinners = [],
    attendees: initialAttendees = [],
    eligibleRegistrantCount,
    selfRevealEnabled: initialSelfReveal = false,
    luckyDrawAppearance: initialAppearance = {},
  } = usePage<PageProps>().props;

  const [rules, setRules] = useState<EligibilityRule[]>(initialRules);
  const [fieldSelections, setFieldSelections] = useState<Record<string, string[]>>(
    initialFieldSelections
  );
  const [categories, setCategories] = useState<Category[]>(initialCategories);
  const [attendees, setAttendees] = useState<Attendee[]>(initialAttendees);
  const [winners, setWinners] = useState<Winner[]>(initialWinners);
  const [selfRevealEnabled, setSelfRevealEnabled] = useState(initialSelfReveal);
  const [eligibleCount, setEligibleCount] = useState<number>(
    eligibleRegistrantCount ?? initialAttendees.length
  );
  const [appearance, setAppearance] = useState<LuckyDrawAppearance>(initialAppearance);

  const tabs = [
    {
      key: "settings",
      label: <TabIconLabel icon={<HiOutlineCog className="text-lg" />} label="Pre-Lucky Draw Settings" />,
      children: (
        <PreLuckyDrawSettings
          eligibilityRules={rules}
          onEligibilityRulesChange={setRules}
          filterOptions={filterOptions}
          fieldSelections={fieldSelections}
          onFieldSelectionsChange={setFieldSelections}
          categories={categories}
          onCategoriesChange={setCategories}
          attendees={attendees}
          onAttendeesChange={setAttendees}
          winners={winners}
          onWinnersChange={setWinners}
          eligibleCount={eligibleCount}
          onEligibleCountChange={setEligibleCount}
          eventSlug={eventSlug}
        />
      ),
    },
    {
      key: "big-screen",
      label: <TabIconLabel icon={<HiOutlineDesktopComputer className="text-lg" />} label="Draw on Big Screen" />,
      children: (
        <DrawOnBigScreen
          categories={categories}
          eventSlug={eventSlug}
          onCategoriesChange={setCategories}
          onWinnersChange={setWinners}
        />
      ),
    },
    {
      key: "appearance",
      label: <TabIconLabel icon={<HiOutlineColorSwatch className="text-lg" />} label="Big Screen Appearance" />,
      children: (
        <LuckyDrawAppearanceSettings
          eventSlug={eventSlug}
          appearance={appearance}
          onAppearanceChange={setAppearance}
        />
      ),
    },
    {
      key: "attendee-app",
      label: <TabIconLabel icon={<HiOutlineDeviceMobile className="text-lg" />} label="Draw on Attendee App" />,
      children: (
        <DrawOnAttendeeApp
          selfRevealEnabled={selfRevealEnabled}
          onToggle={setSelfRevealEnabled}
          eventSlug={eventSlug}
        />
      ),
    },
    {
      key: "winners",
      label: <TabIconLabel icon={<HiOutlineClipboardList className="text-lg" />} label="Winners List" />,
      children: <WinnersList winners={winners} eventSlug={eventSlug} />,
    },
  ];

  return (
    <EventLayout>
      <TabbedPage
        title="Lucky Draw"
        subtitle="Configure eligibility rules and broadcast winners on the big screen."
        tabs={tabs}
        defaultActiveKey="settings"
        showTabContainer={false}
        containerClassName="px-6"
      />
    </EventLayout>
  );
};

export default LuckyDrawIndex;
